import { Box, FormControl, IconButton, Stack, TextField, Typography } from '@chronosphereio/chrono-ui';
import { Debounce } from '@chronosphereio/shared';
import { produce } from 'immer';
import {
  DeleteOutline as DeleteIcon,
  EyeOutline as VisibilityIcon,
  EyeOffOutline as VisibilityOffIcon,
} from 'mdi-material-ui';
import { useState } from 'react';
import { FormulaDefinition } from '@/core';
import { useFormulaParser } from '@/plugin-system/runtime/DataQueriesProvider/formulas';
import { AliasInput } from './AliasInput';

const formulaRefBoxSize = 32; // Same size as the input

class InvalidVariableReferenceError extends Error {
  constructor(variable: string) {
    super(`Invalid query ${variable} referenced`);
    this.name = 'InvalidQueryReferenceError';
  }
}

interface FormulaInputProps {
  formula: FormulaDefinition;
  idx: number;
  onChange: (idx: number, formula: FormulaDefinition) => void;
  onDelete: (idx: number) => void;
  onToggleDisabled: (idx: number) => void;
  validVariables: Set<string>;
}

export function FormulaInput(props: FormulaInputProps) {
  const { formula, idx, onChange, onDelete, onToggleDisabled, validVariables } = props;
  const isDisabled = formula.spec.disabled ?? false;

  const [error, setError] = useState<string | undefined>(undefined);

  const { data: parseFormula, isLoading } = useFormulaParser({ enabled: true });
  if (isLoading) {
    return <Typography>Loading formulas... </Typography>;
  }

  const handleAliasChange = (alias: string) => {
    onChange(
      idx,
      produce(formula, (draft) => {
        draft.spec.alias = alias.trim();
      })
    );
  };

  const onChangeWithValidation = (idx: number, value: string) => {
    try {
      const { variableReferences } = parseFormula!({ kind: 'Formula', spec: { expression: value } });
      variableReferences.forEach((variable) => {
        if (validVariables && !validVariables.has(variable)) {
          throw new InvalidVariableReferenceError(variable);
        }
      });
      onChange(
        idx,
        produce(formula, (draft) => {
          draft.spec.expression = value.trim();
        })
      );
      setError(undefined);
    } catch (e) {
      if (e instanceof InvalidVariableReferenceError) {
        setError(e.message);
      } else {
        setError('This formula is invalid, please check the syntax');
      }
    }
  };

  return (
    <FormControl margin="dense">
      <Stack direction="row" alignItems="center" spacing={1} marginLeft="auto">
        <AliasInput alias={formula.spec.alias ?? ''} onChange={handleAliasChange} />
        <div>
          <IconButton size="small" aria-label={`Hide formula ${idx}`} onClick={() => onToggleDisabled(idx)}>
            {isDisabled ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </div>
        <div>
          <IconButton size="small" aria-label={`Delete formula ${idx}`} onClick={() => onDelete(idx)}>
            <DeleteIcon />
          </IconButton>
        </div>
      </Stack>
      <Stack sx={{ marginTop: '8px' }} spacing={1} direction="row">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius={1}
          width={formulaRefBoxSize}
          height={formulaRefBoxSize}
          sx={{
            backgroundColor: (t) => t.palette.status.default.lowContrast,
          }}
        >
          <Typography variant="body1" fontWeight={'bold'}>
            #
          </Typography>
        </Box>
        <Box width={'100%'} display="flex" flexDirection="column">
          <Debounce value={formula.spec.expression} onChange={(value) => onChangeWithValidation(idx, value)}>
            {(value, setValue) => (
              <>
                <TextField
                  fullWidth={true}
                  placeholder={'Enter your formula, e.g. (a + b) * 2'}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  error={!!error}
                />
                {error && (
                  <Typography aria-label="formula-error" variant="body2" color="error" marginTop={1}>
                    {error}
                  </Typography>
                )}
              </>
            )}
          </Debounce>
        </Box>
      </Stack>
    </FormControl>
  );
}
